import type { ComponentType, ReactNode } from 'react';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';

/**
 * Navegação para as telas de chamado.
 *
 * O `to` do TanStack Router é tipado contra a árvore registrada em
 * `router.tsx`, e este módulo existe antes de a rota ser registrada lá — o
 * `Link` recusaria `/chamados/$ticketId` na compilação. A conversão de tipo
 * fica concentrada aqui, num arquivo só, em vez de espalhada por cada tela.
 *
 * Depois da integração, o corpo destas funções vira um `<Link>` direto e a
 * conversão some sem tocar em mais nada.
 */

export const CAMINHO_LISTA = '/chamados';
export const CAMINHO_DETALHE = '/chamados/$ticketId';

/**
 * As abas do detalhe, e o fato de a aba corrente morar na URL.
 *
 * Antes, a aba era `useState` — e isso bastava enquanto ninguém precisava
 * apontar para uma delas de fora. Precisa agora: o calendário de mudanças
 * manda o comitê direto para a ficha de Change Enablement de um chamado, e um
 * link que abre a aba errada é um link que obriga a pessoa a procurar.
 *
 * Com a aba na URL, o endereço também pode ser mandado por mensagem e
 * sobrevive a recarregar — as duas coisas que se faz com um chamado durante
 * uma reunião.
 */
export const ABAS_DO_CHAMADO = [
  'descricao',
  'mudanca',
  'subchamados',
  'comentarios',
  'historico',
  'sla',
  'tempo',
  'anexos',
  'escalonamentos',
] as const;

export type AbaDoChamado = (typeof ABAS_DO_CHAMADO)[number];

/** `?aba=` inválida é tratada como ausente — a tela cai na primeira aba. */
export function abaValida(valor: unknown): AbaDoChamado | null {
  return typeof valor === 'string' && (ABAS_DO_CHAMADO as readonly string[]).includes(valor)
    ? (valor as AbaDoChamado)
    : null;
}

interface PropsDeLink {
  to: string;
  params?: Record<string, string>;
  className?: string;
  title?: string;
  children: ReactNode;
}

const LinkDeRota = Link as unknown as ComponentType<PropsDeLink>;

export function LinkDeChamado({
  ticketId,
  className,
  title,
  children,
}: {
  ticketId: string;
  className?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <LinkDeRota to={CAMINHO_DETALHE} params={{ ticketId }} className={className} title={title}>
      {children}
    </LinkDeRota>
  );
}

export function LinkDaLista({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <LinkDeRota to={CAMINHO_LISTA} className={className}>
      {children}
    </LinkDeRota>
  );
}

/**
 * A aba corrente do detalhe, lida e escrita na URL.
 *
 * `replace` para trocar de aba não encher o histórico: voltar precisa sair do
 * chamado, e não desfazer sete cliques em abas.
 */
export function useAbaDoChamado(
  ticketId: string,
): { aba: AbaDoChamado; definir: (aba: AbaDoChamado) => void } {
  const router = useRouter();
  const busca = useRouterState({ select: (estado) => estado.location.search }) as { aba?: unknown };

  const navegar = router.navigate as unknown as (destino: {
    to: string;
    params: Record<string, string>;
    search: Record<string, string>;
    replace: boolean;
  }) => Promise<void>;

  return {
    aba: abaValida(busca.aba) ?? 'descricao',
    definir: (aba) => {
      void navegar({
        to: CAMINHO_DETALHE,
        params: { ticketId },
        search: aba === 'descricao' ? {} : { aba },
        replace: true,
      });
    },
  };
}

/** Navegação imperativa — usada depois de abrir um chamado, para ir ao detalhe. */
export function useIrParaChamado(): (ticketId: string) => void {
  const router = useRouter();
  const navegar = router.navigate as unknown as (destino: {
    to: string;
    params: Record<string, string>;
  }) => Promise<void>;

  return (ticketId) => {
    void navegar({ to: CAMINHO_DETALHE, params: { ticketId } });
  };
}

/**
 * O termo de busca vive na URL, não no estado da lista.
 *
 * A casca do aplicativo tem uma busca global que navega para
 * `/chamados?q=…`; se a tela guardasse o termo só em `useState`, chegar por ali
 * abriria a lista sem filtro nenhum e o usuário veria a busca ser engolida.
 * Com a URL como fonte única, os dois caminhos — o campo da tela e o da casca
 * — terminam no mesmo lugar, e o endereço pode ser compartilhado.
 */
export function useBuscaDaLista(): { termo: string; definir: (termo: string) => void } {
  const router = useRouter();
  const busca = useRouterState({ select: (estado) => estado.location.search }) as {
    q?: unknown;
  };

  const navegar = router.navigate as unknown as (destino: {
    to: string;
    search: Record<string, string>;
    replace: boolean;
  }) => Promise<void>;

  return {
    termo: typeof busca.q === 'string' ? busca.q : '',
    definir: (termo) => {
      // `replace` para a busca não encher o histórico: voltar precisa sair da
      // lista, não desfazer letra por letra o que foi pesquisado.
      void navegar({ to: CAMINHO_LISTA, search: termo ? { q: termo } : {}, replace: true });
    },
  };
}
